// 브라우저 웹푸시 구독 관리. 이 기기의 알림 허용/구독 상태를 관리하고 UI에 통지한다.
import { api } from './ApiClient';

type Listener = () => void;

/** base64url VAPID 공개키 → applicationServerKey(Uint8Array) */
function urlBase64ToUint8Array(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export class PushManager {
  private readonly _supported =
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window;
  private _active = false;
  private reg: ServiceWorkerRegistration | null = null;
  private readonly listeners = new Set<Listener>();

  get supported(): boolean {
    return this._supported;
  }
  /** 이 기기가 알림 허용 + 구독 상태인가 */
  get active(): boolean {
    return this._active;
  }

  onChange(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }
  private setActive(v: boolean): void {
    if (this._active !== v) {
      this._active = v;
      this.emit();
    }
  }

  private async registration(): Promise<ServiceWorkerRegistration> {
    if (!this.reg) this.reg = await navigator.serviceWorker.register('/sw.js');
    return this.reg;
  }

  /** 서비스워커 등록 + 현재 구독 상태 반영 (로그인 후 1회 호출) */
  async init(): Promise<void> {
    if (!this._supported) return;
    const reg = await this.registration();
    const sub = await reg.pushManager.getSubscription();
    this.setActive(Notification.permission === 'granted' && !!sub);
  }

  /** 권한 요청 → 구독 → 서버 저장. 아이콘 비활성 상태에서 클릭 시 호출. */
  async enable(): Promise<void> {
    if (!this._supported) throw new Error('이 브라우저는 웹푸시를 지원하지 않습니다.');
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') throw new Error('알림 권한이 허용되지 않았습니다.');

    const reg = await this.registration();
    const { publicKey } = await api.pushPublicKey();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    await api.pushSubscribe(sub.toJSON());
    this.setActive(true);
  }

  /** 구독 해제 → 서버 삭제. 아이콘 활성 상태에서 클릭 시 호출. */
  async disable(): Promise<void> {
    if (!this._supported) return;
    const reg = await this.registration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api.pushUnsubscribe(sub.endpoint);
      await sub.unsubscribe();
    }
    this.setActive(false);
  }
}

export const push = new PushManager();
